import _ from 'underscore';
import React, {
  useCallback, useContext, useMemo, useState,
} from 'react';
import {
  Menu,
  MenuButtonProps,
  MenuItem,
  MenuPopover,
  MenuTrigger,
  SplitButton,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import { PersonSupport16Regular } from '@fluentui/react-icons';

import { LiveAgentQueue } from '@bingads-webui-react/msa-copilot-common';
import { I18nContext, I18nModel } from '@bingads-webui/copilot-i18n';
import { dispatchStructuredLogUserEngagementEvent } from '@bingads-webui-react/copilot-logger';
import { getLiveAgentQueueList, liveAgentQueueOptions } from '../helpers';

const useLiveAgentQueueMenuStyles = makeStyles({
  root: {
    marginTop: tokens.spacingVerticalS,
  },
  menuButton: {
    marginTop: tokens.spacingVerticalS,
    minWidth: '220px',
    justifyContent: 'initial',
  },
  menuList: {
    minWidth: '210px',
  },
  menuItem: {
    minHeight: 0,
    paddingTop: '4px',
    paddingBottom: '4px',
  },
});

export interface LiveAgentQueueMenuProps {
  locale?: string;
  isEnglishOnlyChatSupportAvailable?: boolean;
  disabled?: boolean;
  isIntelligentRoutingEnabled?: boolean;
  onQueueSelected: (queue: LiveAgentQueue) => void;
}

export const LiveAgentQueueMenu: React.FC<LiveAgentQueueMenuProps> = ({
  locale,
  isEnglishOnlyChatSupportAvailable = false,
  disabled,
  isIntelligentRoutingEnabled,
  onQueueSelected,
}) => {
  const i18n = useContext(I18nContext) as I18nModel;
  const styles = useLiveAgentQueueMenuStyles();

  const [open, setOpen] = useState<boolean>(false);
  const [selectedQueue, setSelectedQueue] = useState<LiveAgentQueue | undefined>(undefined);

  const queueList = useMemo(() => getLiveAgentQueueList(locale!, isIntelligentRoutingEnabled), [locale]);

  const primaryActionButtonProps = useMemo(() => ({
    onClick: () => setOpen(!open),
  }), [open]);

  const handleQueueSelected = useCallback((queue: LiveAgentQueue) => {
    setSelectedQueue(queue);
    onQueueSelected(queue);
    dispatchStructuredLogUserEngagementEvent({
      activity: {} as any,
      options: {
        liveAgentQueueSelected: {
          queue: queue as string,
        },
      },
    });
  }, [onQueueSelected]);

  const queueButtonLabel = useMemo(() => {
    if (selectedQueue) {
      return i18n.getString(_.findWhere(liveAgentQueueOptions, { queue: selectedQueue })?.label!);
    }
    if (isEnglishOnlyChatSupportAvailable) {
      return i18n.getString(_TL_('Choose topic (English)'));
    }
    return i18n.getString(_TL_('Choose topic'));
  }, [selectedQueue, isEnglishOnlyChatSupportAvailable, i18n]);

  return (
    <div className={mergeClasses('live-agent-queue-menu', styles.root)}>
      <Menu
        open={open}
        positioning="below"
        onOpenChange={(_e, data) => setOpen(data.open)}
      >
        <MenuTrigger>
          {(triggerProps: MenuButtonProps) => (
            <SplitButton
              icon={<PersonSupport16Regular />}
              className={mergeClasses('live-agent-queue-menu-split-button', styles.menuButton)}
              menuButton={triggerProps}
              primaryActionButton={primaryActionButtonProps}
              disabled={disabled || !!selectedQueue}
            >
              {queueButtonLabel}
            </SplitButton>
          )}
        </MenuTrigger>
        <MenuPopover className={styles.menuList}>
          {_.map(queueList, (option) => (
            <MenuItem
              key={option.queue}
              className={mergeClasses(`live-agent-queue-menu-item-${option.queue}`, styles.menuItem)}
              onClick={() => handleQueueSelected(option.queue)}
            >
              {i18n.getString(option.label)}
            </MenuItem>
          ))}
        </MenuPopover>
      </Menu>
    </div>
  );
};
